import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { io } from "socket.io-client";
import { Icon } from "@iconify/react";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { IconButton, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import "./LiveChat.css";
import MessageList from "../Chat/MessageList";
import ModalEndChatLiveChat from "../Chat/ModalEndChatLiveChat";
import Loader from "../Components/Loader";
import LogoMoxcal32 from "../Images/XOnly32.png";

import {
  addAttachment,
  createTicket,
  endLiveChatSession,
  getDetails,
  getSettings,
  sendLiveChatMessage,
  uploadFile,
} from "../Store/Actions/LiveChatAction";

import { UPDATE_CHAT } from "../Store/Types/ChatTypes";

export default function LiveChat() {
  const initialAttachment = {
    blob: null,
    file: "",
    mimetype: "",
    name: "",
    size: null,
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [attachment, setAttachment] = useState(initialAttachment);
  const [chatMessage, setChatMessage] = useState("");

  const [customerData, setCustomerData] = useState({
    name: "",
    email: "",
    phone: "",
    ref: "",
    message: "",
  });

  const [details, setDetails] = useState(null);
  const [hideEmojiStickerDisplay, setHideEmojiStickerDisplay] = useState(true);
  const [isEndChatModalShown, setIsEndChatModalShown] = useState(false);

  // To show loading state on confirm close chat button
  const [isEndingChat, setIsEndingChat] = useState(false);

  const [isFirstChat, setIsFirstChat] = useState(true);
  const [isInOfficeHour, setIsInOfficeHour] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNoOnlineAgent, setIsNoOnlineAgent] = useState(false);

  const [isNoResponseMessageShown, setIsNoResponseMessageShown] =
    useState(false);

  const [isSending, setIsSending] = useState(false);
  const [isTicketCreated, setIsTicketCreated] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [lastInteractionTime, setLastInteractionTime] = useState(null);
  const [settings, setSettings] = useState(null);
  const [toggleEmojiSticker, setToggleEmojiSticker] = useState("emoji");
  const dispatch = useDispatch();
  const lastInteractionRef = useRef(lastInteractionTime);
  const socketRef = useRef(null);

  // keep the lastInteraction ref updated whenever the state changes
  useEffect(() => {
    lastInteractionRef.current = lastInteractionTime;
  }, [lastInteractionTime]);

  useEffect(() => {
    socketRef.current = io(`${process.env.REACT_APP_SOCKET_URL}`);
    const socket = socketRef.current;

    socket.on("connect", () => {
      let chatListID = localStorage.getItem("chatListID"),
        sessionID = localStorage.getItem("sessionID");

      if (chatListID) {
        fetchDetails(chatListID);
        socket.emit("startLiveChatSession", sessionID);
        setIsFirstChat(false);
      }
    });

    socket.on("closeSession", onCloseSession);

    socket.on("sendLiveChatMessage", () => {
      setIsTyping(false);
      dispatch({ type: UPDATE_CHAT });
      sendNotification("newMessage");
    });

    socket.on(
      "stopTyping",
      (data) => data.from === "agent" && setIsTyping(false)
    );

    socket.on("typing", (data) => data.from === "agent" && setIsTyping(true));

    // Check for user inactivity every 1 minute after session start
    let interval;

    if (!isFirstChat) {
      interval = setInterval(() => {
        const minutesPassed = moment().diff(
            lastInteractionRef.current,
            "minutes"
          ),
          noResponseTimeOut = settings?.noresponsetimeout ?? 30,
          autoCloseTimeout = settings?.autoclosetimeout ?? 60;

        if (minutesPassed >= autoCloseTimeout) {
          handleEndLiveChatSession();
          clearInterval(interval);
        } else if (minutesPassed === noResponseTimeOut) {
          setIsNoResponseMessageShown(true);
          sendNotification("noResponse");
        }
      }, 1000);
    }

    return () => {
      socket.off("closeSession", onCloseSession);
      socket.off("sendLiveChatMessage");
      socket.off("stopTyping");
      socket.off("typing");
      clearInterval(interval);
    };
  }, [isFirstChat]);

  useEffect(() => {
    // Ask for notification permission on load
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }

    fetchSettings();
  }, []);

  const open = Boolean(anchorEl);

  const audioTypes = [
    "audio/aac",
    "audio/mp4",
    "audio/mpeg",
    "audio/amr",
    "audio/ogg",
  ];

  const documentTypes = [
    "text/plain",
    "application/pdf",
    "application/vnd.ms-powerpoint",
    "application/msword",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const imageTypes = ["image/jpeg", "image/png"];
  const stickerTypes = ["image/webp"];
  const videoTypes = ["video/mp4", "video/3gp"];

  const emojis = [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "😊",
    "😇",
    "🙂",
    "🙃",
    "😉",
    "😌",
    "😍",
    "😝",
    "😜",
    "🧐",
    "🤓",
    "😎",
    "😕",
    "🤑",
    "🥴",
    "😱",
  ];

  const stickers = [
    "https://newflat.nyc3.cdn.digitaloceanspaces.com/G03_w_sti/sticker-fan_13099335_o.webp",
    "https://newflat.nyc3.cdn.digitaloceanspaces.com/G03_w_sti/sticker-fan_13099342_o.webp",
    "https://newflat.nyc3.cdn.digitaloceanspaces.com/G03_w_sti/sticker-fan_13099330_o.webp",
    "https://newflat.nyc3.cdn.digitaloceanspaces.com/G03_w_sti/sticker-fan_13099327_o.webp",
  ];

  function createNewSession() {
    fetchSettings();

    setCustomerData({
      name: "",
      email: "",
      phone: "",
      ref: "",
      message: "",
    });

    setDetails(null);
    localStorage.removeItem("chatListID");
    localStorage.removeItem("sessionID");
    setIsFirstChat(true);
  }

  // debounce stop typing
  let typingTimeout;

  function debounceStopTyping(sessionID) {
    clearTimeout(typingTimeout);

    typingTimeout = setTimeout(() => {
      socketRef.current.emit("stopTyping", {
        from: "customer",
        sessionID: sessionID,
      });
    }, 500);
  }

  function fetchDetails(chatListID) {
    getDetails(chatListID)
      .then((response) => {
        if (response.status) {
          console.log("Details: ", response.data);
          setDetails(response.data);
        } else {
          console.log("Error get details", response.error);
        }
      })
      .catch((err) => {
        console.log("Error get details", err);
      });
  }

  function fetchSettings() {
    getSettings(10)
      .then((response) => {
        if (response.status) {
          console.log("Settings: ", response.data);
          setSettings(response.data);

          if (
            moment().isBetween(
              moment().hour(response.data.openhour),
              moment().hour(response.data.closehour)
            )
          ) {
            setIsInOfficeHour(true);
          } else {
            setIsInOfficeHour(false);
          }

          setIsLoading(false);
        } else {
          console.log("Error get settings", response.error);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("Error get settings", err);
        setIsLoading(false);
      });
  }

  // File size formatter
  function formatBytes(bytes) {
    const sizeSuffixes = ["B", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return (
      (!bytes && "0 Bytes") ||
      (bytes / Math.pow(1024, i)).toFixed(2) + " " + sizeSuffixes[i]
    );
  }

  function handleCreateTicket() {
    setIsSending(true);

    createTicket({
      description: customerData.message.trim(),
      phoneno: customerData.phone,
      email: customerData.email,
    })
      .then((response) => {
        if (response.status) {
          console.log("Output caseID", response.data.caseid);
          setIsSending(false);
          setIsTicketCreated(true);
        } else {
          console.log("error", response.error);
          setIsSending(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSending(false);
      });
  }

  function handleEndLiveChatSession() {
    setIsEndingChat(true);

    endLiveChatSession({ sessionid: localStorage.getItem("sessionID") })
      .then((response) => {
        if (response.status) {
          onCloseSession();
          console.log("Success: ", response.data);
          setIsEndingChat(false);
        } else {
          console.log("Error ending chat", response.error);
          setIsEndingChat(false);
        }
      })
      .catch((err) => {
        console.log("Error ending chat", err);
        setIsEndingChat(false);
      });
  }

  function handleMessageInputChange(sessionID) {
    socketRef.current.emit("typing", {
      from: "customer",
      sessionID: sessionID,
    });

    debounceStopTyping(sessionID);
  }

  function handleSendMessage() {
    setLastInteractionTime(moment());
    setIsSending(true);

    if (attachment.file) {
      // Upload file
      const formData = new FormData();
      formData.append("files", attachment.blob, attachment.name);

      uploadFile(formData)
        .then((responseUploadFile) => {
          if (responseUploadFile) {
            console.log("file uploaded");
            const now = moment();
            const year = now.format("YYYY");
            const month = now.format("MM");
            const date = now.format("DD");
            let filetype;
            const mime = attachment.mimetype.toLowerCase();
            if (mime.startsWith("image/")) filetype = "image";
            if (mime.startsWith("video/")) filetype = "video";
            if (mime.startsWith("audio/")) filetype = "audio";

            // documents (common office & pdf types)
            if (
              mime === "application/pdf" ||
              mime === "application/msword" ||
              mime ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || // .docx
              mime === "application/vnd.ms-excel" ||
              mime ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || // .xlsx
              mime === "application/vnd.ms-powerpoint" ||
              mime ===
                "application/vnd.openxmlformats-officedocument.presentationml.presentation" || // .pptx
              mime === "text/plain"
            ) {
              filetype = "document";
            }

            // Add attachment
            addAttachment({
              filetype: filetype,
              filename: `/live-chat/${year}/${month}/${date}/${attachment.name}`,
            })
              .then((responseAddAttachment) => {
                if (responseAddAttachment.status) {
                  console.log("Attachment added");

                  // Send message
                  sendLiveChatMessage({
                    // Temp hardcoded values
                    chatlistid: localStorage.getItem("chatListID"),
                    sessionid: localStorage.getItem("sessionID"),
                    agentid: details.agentid,
                    livechatid: details.livechatid,
                    chatbotid: details.chatbotid,
                    maindomain: details.maindomain,
                    promptname: customerData.name,
                    promptphone: customerData.phone,
                    promptemail: customerData.email,
                    promptrefno: customerData.ref,
                    chatbotsessionid: null,
                    aichatbotsessionid: null,
                    type: filetype,
                    bodytext: chatMessage.trim(),
                    messagefrom: "CUSTOMER",
                    mediaid: responseAddAttachment.data,
                  })
                    .then((responseSendMessage) => {
                      if (responseSendMessage.status) {
                        setIsSending(false);
                        setAttachment(initialAttachment);
                        setChatMessage("");

                        // Refresh chat list and message list
                        dispatch({ type: UPDATE_CHAT });
                      } else {
                        console.log(
                          "error send message",
                          responseSendMessage.error
                        );
                        setIsSending(false);
                      }
                    })
                    .catch((err) => {
                      console.log("err", err);
                      setIsSending(false);
                    });
                } else {
                  console.log(
                    "error add attachment",
                    responseAddAttachment.error
                  );
                }
              })
              .catch((err) => {
                console.log("err", err);
              });
          } else {
            console.log("error upload file", responseUploadFile.error);
          }
        })
        .catch((err) => {
          console.log("err", err);
        });
    } else {
      setChatMessage("");

      // Set a timeout to setIsSending(false) after 10 seconds
      const timeout = setTimeout(() => {
        setIsSending(false);
        console.log("Timeout: Message sending took too long.");
      }, 10000);

      sendLiveChatMessage({
        // Temp hardcoded values
        chatlistid: localStorage.getItem("chatListID"),
        sessionid: localStorage.getItem("sessionID"),
        agentid: details.agentid,
        livechatid: details.livechatid,
        chatbotid: details.chatbotid,
        maindomain: details.maindomain,
        promptname: customerData.name,
        promptphone: customerData.phone,
        promptemail: customerData.email,
        promptrefno: customerData.ref,
        chatbotsessionid: null,
        aichatbotsessionid: null,
        type: "text",
        bodytext: chatMessage.trim(),
        messagefrom: "CUSTOMER",
        mediaid: null,
      })
        .then((response) => {
          if (response.status) {
            console.log("Success: ", response.data);
            setChatMessage("");
            setIsSending(false);
            clearTimeout(timeout); // Clear the timeout as the response is received
            dispatch({ type: UPDATE_CHAT });
          } else {
            console.log("Error send message", response.error);
            setIsSending(false);
            clearTimeout(timeout); // Clear the timeout as the response is received
          }
        })
        .catch((err) => {
          console.log("Error send message", err);
          setIsSending(false);
          clearTimeout(timeout); // Clear the timeout as the response is received
        });
    }
  }

  function onCloseSession() {
    setDetails((prevDetails) => ({
      ...prevDetails,
      issessionactive: false,
      inactivetime: moment(),
    }));
  }

  function onFileChange(event) {
    let file = event.target.files[0];
    let reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      setAttachment({
        blob: file,
        file: reader.result,
        mimetype: file.type,
        name: file.name,
        size: file.size,
      });
    };
  }

  function renderPreviewFile() {
    if (audioTypes.includes(attachment.mimetype)) {
      return <audio controls src={attachment.file} />;
    } else if (documentTypes.includes(attachment.mimetype)) {
      return <Icon height="200" icon="mingcute:document-2-fill" />;
    } else if (
      imageTypes.includes(attachment.mimetype) ||
      stickerTypes.includes(attachment.mimetype)
    ) {
      return (
        <img src={attachment.file} alt="" className="preview-image-container" />
      );
    } else if (videoTypes.includes(attachment.mimetype)) {
      return (
        <video controls>
          <source src={attachment.file} type={attachment.mimetype} />
          Your browser does not support the video tag.
        </video>
      );
    }
  }

  function sendNotification(type) {
    // If tab is inactive, show notification
    if (
      document.visibilityState === "hidden" &&
      Notification.permission === "granted"
    ) {
      new Notification("Pesan live chat baru", {
        body:
          type === "newMessage"
            ? "Anda memiliki pesan live chat baru"
            : settings?.noresponsemessage ??
              "Perhatian: Sesi live chat ini akan ditutup otomatis jika Anda tidak merespon. Terima kasih.",

        icon: LogoMoxcal32,
      });
    }
  }

  function startChat() {
    let sessionID = crypto.randomUUID();
    setIsSending(true);
    localStorage.setItem("sessionID", sessionID);

    sendLiveChatMessage({
      // Temp hardcoded values
      sessionid: sessionID,
      livechatid: 10,
      chatbotid: settings.chatbotid,
      maindomain: "https://trial1.com/",
      promptname: customerData.name,
      promptphone: customerData.phone,
      promptemail: customerData.email,
      promptrefno: customerData.ref,
      chatbotsessionid: null,
      aichatbotsessionid: null,
      type: "text",
      bodytext: customerData.message.trim(),
      messagefrom: "CUSTOMER",
      mediaid: null,
    })
      .then((response) => {
        if (response.status) {
          console.log("Success: ", response.data);
          localStorage.setItem("chatListID", response.data);
          fetchDetails(response.data);
          dispatch({ type: UPDATE_CHAT });
          setLastInteractionTime(moment());
          setIsFirstChat(false);
          setIsSending(false);
          settings?.chatbotid && setIsTyping(true);
        } else {
          console.log("Error start chat", response.error);
          setIsSending(false);
        }
      })
      .catch((err) => {
        console.log("Error start chat", err);
        setIsSending(false);
      });
  }

  function TriggerClick(id) {
    let el = document.getElementById(id);
    if (el.onclick) {
      el.onclick();
    } else if (el.click) {
      el.click();
    }
  }

  return (
    <div className="live-chat chat-room" id="chatroom_listmessage">
      {isLoading ? (
        <Loader />
      ) : isNoOnlineAgent ? (
        <div class="no-online-agent">
          <div>
            <Icon icon="mdi:face-agent" width="100" height="100" />
            <Icon icon="eos-icons:hourglass" width="100" height="100" />
          </div>

          <p>{settings.offlinemessage}</p>
        </div>
      ) : (
        <>
          {attachment.file && (
            <div className="preview-file-overlay">
              <CloseOutlinedIcon
                className="close-preview-button"
                sx={{ fontSize: 36 }}
                onClick={() => setAttachment(initialAttachment)}
              />

              {renderPreviewFile()}

              <p className="preview-file-details">
                {attachment.name} - {formatBytes(attachment.size)}
              </p>
            </div>
          )}

          <header className="live-chat-header">
            <div className="avatar">
              <Icon icon="mdi:face-agent" width="24" height="24" />
            </div>

            <p>{isFirstChat ? "Butuh bantuan?" : "Live Chat"}</p>

            <IconButton
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
              }}
            >
              <Icon icon="nrk:more" width="24" height="24" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              id="endChatButton"
              open={open}
              onClose={() => setAnchorEl(null)}
              onClick={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: "center", vertical: "top" }}
              anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            >
              <MenuItem
                disabled={!details?.issessionactive}
                sx={{
                  fontSize: "14px",
                  padding: "6px 14px",
                }}
                onClick={() => setIsEndChatModalShown(true)}
              >
                End Chat
              </MenuItem>
            </Menu>
          </header>

          <div className="chat-room-body">
            <div className="chat-room-body-content">
              {isFirstChat ? (
                isTicketCreated ? (
                  <div class="no-online-agent">
                    <div>
                      <Icon
                        icon="line-md:email-check"
                        width="100"
                        height="100"
                      />
                    </div>

                    <p>
                      Kami akan segera menghubungi Anda kembali. Terima kasih!
                    </p>
                  </div>
                ) : (
                  <form
                    className="customer-data-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      isInOfficeHour ? startChat() : handleCreateTicket();
                    }}
                  >
                    {!isInOfficeHour && (
                      <p>{settings.outofofficehourmessage}</p>
                    )}

                    {settings.ispromtname && (
                      <TextField
                        fullWidth
                        label="Masukkan nama Anda"
                        required
                        value={customerData.name}
                        onChange={(e) =>
                          setCustomerData({
                            ...customerData,
                            name: e.target.value,
                          })
                        }
                      />
                    )}

                    {settings.ispromtemail && (
                      <TextField
                        fullWidth
                        label="Masukkan alamat email Anda"
                        required={!customerData.phone}
                        type="email"
                        value={customerData.email}
                        onChange={(e) =>
                          setCustomerData({
                            ...customerData,
                            email: e.target.value,
                          })
                        }
                      />
                    )}

                    {settings.ispromtphone && (
                      <TextField
                        fullWidth
                        label="Masukkan nomor telepon Anda"
                        required={!customerData.email}
                        type="tel"
                        value={customerData.phone}
                        onChange={(e) =>
                          setCustomerData({
                            ...customerData,
                            phone: e.target.value,
                          })
                        }
                      />
                    )}

                    {settings.ispromtrefno && (
                      <TextField
                        fullWidth
                        label="Masukkan no. referensi (optional)"
                        value={customerData.ref}
                        onChange={(e) =>
                          setCustomerData({
                            ...customerData,
                            ref: e.target.value,
                          })
                        }
                      />
                    )}

                    <TextField
                      fullWidth
                      label="Apa yang mau Anda tanyakan?"
                      minRows={1}
                      multiline
                      required
                      value={customerData.message}
                      onChange={(e) =>
                        setCustomerData({
                          ...customerData,
                          message: e.target.value,
                        })
                      }
                    />

                    <button
                      className="btnSubmit"
                      disabled={isSending}
                      type="submit"
                    >
                      <span>
                        {isSending
                          ? "Mengirim..."
                          : isInOfficeHour
                          ? "Mulai chat"
                          : "Submit"}
                      </span>
                    </button>
                  </form>
                )
              ) : (
                <>
                  {isNoResponseMessageShown && (
                    <p className="no-response-message">
                      {settings?.noresponsemessage}
                    </p>
                  )}

                  <MessageList
                    chatListID={localStorage.getItem("chatListID")}
                    customerData={customerData}
                    isLiveChat
                    isLiveChatWidget
                    isTyping={isTyping}
                  />
                </>
              )}
            </div>
          </div>

          {!isFirstChat && (
            <div className="chat-box">
              {!details?.issessionactive ? (
                <p className="session-ended">
                  Sesi live chat ini sudah berakhir
                  <br />
                  <span className="time">
                    {moment(details?.inactivetime).format("DD/MM/YY, HH:mm")}
                  </span>
                  <br />
                  <span className="new-session" onClick={createNewSession}>
                    Buat sesi baru
                  </span>
                </p>
              ) : (
                <>
                  <div
                    className="emoji-sticker-container"
                    style={{
                      display: hideEmojiStickerDisplay ? "none" : "block",
                    }}
                  >
                    <nav className="selector">
                      <p
                        className={
                          toggleEmojiSticker === "emoji" ? "selected" : ""
                        }
                        onClick={() => setToggleEmojiSticker("emoji")}
                      >
                        Emoji
                      </p>

                      <p
                        className={
                          toggleEmojiSticker === "sticker" ? "selected" : ""
                        }
                        onClick={() => setToggleEmojiSticker("sticker")}
                      >
                        Sticker
                      </p>
                    </nav>

                    {toggleEmojiSticker === "emoji" ? (
                      <div className="emoji-container">
                        {emojis.map((e) => (
                          <button
                            className="emoji"
                            key={e}
                            onClick={() => setChatMessage(chatMessage + e)}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="sticker-container">
                        {stickers.map((sticker) => (
                          <img
                            alt=""
                            className="sticker"
                            src={sticker}
                            onClick={() => {
                              setHideEmojiStickerDisplay(true);
                              setToggleEmojiSticker("emoji");
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="chat-box-content">
                    <div className="chat-box-buttons">
                      <input
                        accept=".aac,.mp4,.mp3,.amr,.ogg,.txt,.pdf,.ppt,.doc,.xls,.docx,.pptx,.xlsx,.jpeg,.jpg,.webp,.png,.3gp,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,text/plain,application/pdf,application/vnd.ms-powerpoint,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,image/webp,video/mp4,video/3gp"
                        id="inputFileChat"
                        type="file"
                        hidden
                        onClick={(e) => (e.target.value = null)}
                        onChange={onFileChange}
                      />

                      <Tooltip arrow placement="top" title="Attach File">
                        <IconButton
                          className="add-file-button"
                          onClick={() => TriggerClick("inputFileChat")}
                        >
                          <AttachFileOutlinedIcon />
                        </IconButton>
                      </Tooltip>

                      {/* <Tooltip arrow placement="top" title="Add Emoji/Sticker">
                        <IconButton
                          className="add_emote"
                          disabled={audioTypes.includes(attachment.mimetype)}
                          onClick={() => {
                            setHideEmojiStickerDisplay(!hideEmojiStickerDisplay);
                            setHideTemplateDisplay(true);
                          }}
                        >
                          {hideEmojiStickerDisplay ? (
                            <InsertEmoticonOutlinedIcon />
                          ) : (
                            <Icon icon="ph:x-bold" color="#727272" />
                          )}
                        </IconButton>
                      </Tooltip> */}
                    </div>

                    <form className="message-input-container">
                      <TextField
                        className="message-input"
                        // disabled={audioTypes.includes(attachment.mimetype)}
                        InputLabelProps={{ shrink: false }}
                        maxRows={3}
                        multiline
                        placeholder="Ketik pesan..."
                        value={chatMessage}
                        onChange={(e) => {
                          setChatMessage(e.target.value);

                          // handleMessageInputChange(
                          //   localStorage.getItem("sessionID")
                          // );
                        }}
                        onFocus={() => setIsNoResponseMessageShown(false)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (e.shiftKey) {
                              // Shift+Enter → allow newline
                              return;
                            }

                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />

                      <IconButton
                        disabled={isSending}
                        type="submit"
                        onClick={(event) => {
                          event.preventDefault();
                          handleSendMessage();
                        }}
                      >
                        {isSending ? (
                          <Icon
                            height="24"
                            icon="eos-icons:loading"
                            color="#727272"
                          />
                        ) : (
                          <SendOutlinedIcon />
                        )}
                      </IconButton>
                    </form>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {isEndChatModalShown && (
        <ModalEndChatLiveChat
          handleEndChat={handleEndLiveChatSession}
          isEndingChat={isEndingChat}
          onClose={() => setIsEndChatModalShown(false)}
        />
      )}
    </div>
  );
}
